import type { ApiResponse, CreatorProfile, PlatformEnum } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';

const fetchContactProfile = async (
  creatorIdentityId: string,
  platform: PlatformEnum
): Promise<CreatorProfile> => {
  const { data } = await api.get<ApiResponse<CreatorProfile>>(
    `/dashboard/contacts/${creatorIdentityId}/profile`,
    { params: { platform } }
  );

  if (!data.data) {
    throw new Error('Contact profile not found');
  }

  return data.data;
};

/**
 * Lazy profile slice for the unified contact card, keyed by `creatorIdentityId`
 * and the platform the drawer shows: Instagram and TikTok are two profiles of
 * the same person, so each gets its own cache entry. Only fires when the PROFILE
 * view mounts. The backend reuses the creator-api proxy, so the payload is a
 * `CreatorProfile`.
 */
export const useContactProfileQuery = (creatorIdentityId: string | null, platform: PlatformEnum) =>
  useQuery<CreatorProfile, Error>({
    queryKey: ['contact-profile', creatorIdentityId, platform],
    queryFn: () => fetchContactProfile(creatorIdentityId!, platform),
    enabled: !!creatorIdentityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
